// @ts-nocheck
import { formatMoney, getAdvancedProductFormData } from "@theme/utilities";
import { sectionRenderer, normalizeSectionId } from "@theme/section-renderer";
import { Component } from "@theme/component";
import {
  preloadImage,
  onAnimationEnd,
  yieldToMainThread,
} from "@theme/utilities";
import { CartAddEvent, CartErrorEvent } from "@theme/events";
import { DialogComponent, DialogOpenEvent } from "@theme/dialog";
export class AddToCartEngraving extends Component {
  requiredRefs = ["addToCarEngraving"];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
      );
    }
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCarEngraving.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCarEngraving.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    if (this.refs.addToCarEngraving.dataset.puppet !== "true") {
      const animationEnabled = this.dataset.addToCartAnimation === "true";
      if (animationEnabled && !event.target.closest(".quick-add-modal")) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCarEngraving } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCarEngraving || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    let flyToCartClass = addToCarEngraving.classList.contains(
      "quick-add__button",
    )
      ? "fly-to-cart--quick"
      : "fly-to-cart--main";

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.style.setProperty("--start-opacity", "0");
    flyToCartElement.source = addToCarEngraving;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCarEngraving } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
    );
    this.#resetTimeouts = [];

    if (addToCarEngraving.dataset.added !== "true") {
      addToCarEngraving.dataset.added = "true";
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCarEngraving);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCarEngraving.removeAttribute("data-added");

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get("add-to-cart-engraving")) {
  customElements.define("add-to-cart-engraving", AddToCartEngraving);
}

class ProductEngravingProduct extends HTMLElement {
  /** @type {boolean} */
  #canvasResizeBound = false;

  /** @type {() => void} */
  #onCanvasResize = () => {
    this.adjustCanvasSize();
    void this.redrawCanvas(this.activeInput);
  };

  /** @type {() => void} */
  #onSearchParamsChange = () => {
    queueMicrotask(() => void this.#refreshHostSection());
  };

  #refreshHostSection = async () => {
    if (!this.isConnected) return;

    const hostSection = this.closest(".shopify-section");
    if (!hostSection?.id) return;

    const sectionId = normalizeSectionId(hostSection.id);

    try {
      await sectionRenderer.renderSection(sectionId, {
        cache: false,
        url: new URL(window.location.href),
      });
    } catch (error) {
      console.warn("product-engraving: section render failed", error);
    }
  };

  constructor() {
    super();
    this.canvas = null;
    this.ctx = null;
    this.cartDrawer = document.querySelector("cart-drawer-component");
    this.parent = this.closest("form");
    this.totalEngravingPrice = 0;
    this.root = null;
    this.model = this.dataset.model;
    this.baseImage = null;
    this.activeInput = null;
    this.designMode = this.dataset.designMode;
    this.loadedFonts = new Set();
    this.fontLoadingPromises = new Map();
  }

  connectedCallback() {
    if (!history._engravingPatched) {
      const _push = history.pushState.bind(history);
      const _replace = history.replaceState.bind(history);

      history.pushState = (...args) => {
        _push(...args);
        window.dispatchEvent(new Event("searchparamschange"));
      };
      history.replaceState = (...args) => {
        _replace(...args);
        window.dispatchEvent(new Event("searchparamschange"));
      };

      history._engravingPatched = true;
    }

    window.addEventListener("searchparamschange", this.#onSearchParamsChange);
    window.addEventListener("popstate", this.#onSearchParamsChange);

    this.#initializeEngravingUi();
  }

  /**
   * Runs the DOM-bound setup from `connectedCallback` before the host dialog opens,
   * so refs to the product form and controls are fresh before `showDialog`.
   */
  prepareBeforeOpen() {
    if (!this.isConnected) return;
    this.initElements();
  }

  /**
   * Re-measures the canvas after the host dialog is visible (size is often zero while closed).
   */
  syncLayoutAfterDialogOpen() {
    if (!this.isConnected || !this.canvas) return;
    this.adjustCanvasSize();
    if (this.baseImage) {
      void this.redrawCanvas(this.activeInput);
    } else {
      this.loadInitialImage();
    }
    this.updateButtonState();
  }

  #initializeEngravingUi() {
    this.initElements();
    this.setupEventListeners();
    this.initCanvas();
    this.loadInitialImage();
  }

  disconnectedCallback() {
    window.removeEventListener("searchparamschange", this.#onSearchParamsChange);
    window.removeEventListener("popstate", this.#onSearchParamsChange);
    if (this.#canvasResizeBound) {
      window.removeEventListener("resize", this.#onCanvasResize);
      this.#canvasResizeBound = false;
    }
    this.cleanup();
  }

  initElements() {
    this.root = document.querySelector("product-form-component");
    this.inputElements = this.querySelectorAll("input[type='text']");
    this.selectElement = this.querySelectorAll("select");
    this.submitBtn = document.querySelector("add-to-cart-engraving");
    this.submitBtn.disable();
    this.mainForm = this.root.querySelector('form[action="/cart/add"]');
    this.mainProductId = this.mainForm.querySelector('input[name="id"]')?.value;
    if(document.querySelector("pre-order-variant-block")) {
      this.root = document.querySelector('pre-order-variant-block .pre-order-variant__atc-wrap:not([hidden]) product-form-component');
      this.mainForm = this.root.querySelector('form[action="/cart/add"]');
      this.mainProductId = this.mainForm.querySelector('input[name="id"]')?.value;
    }


    this.productPriceElement = this.querySelector(".engraving-price");
    this.productPrice = this.productPriceElement.dataset.price || 0;

    if (this.model === "product") {
      this.closeButton = this.querySelector(".engraving-popup-close");
      this.layer = this.querySelector(".engraving-popup-layer");
      this.engravingBtn = document.querySelector("#engraving-btn");
    }
    if (this.designMode) {
      this.designElements = this.querySelectorAll("input[type='number']");
    }
  }

  cleanup() {
    if (this.inputElements.length) {
      this.inputElements?.forEach((input) => {
        input.value = "";
      });
    }

    if (this.selectElement.length) {
      this.selectElement.forEach((select) => {
        select.selectedIndex = 0;
      });
    }

    if (this.productPriceElement && this.productPrice) {
      this.productPriceElement = `$${this.productPrice?.toFixed(2)}`;
    }
  }

  //  Setup EventListeners
  setupEventListeners() {
    if (this.inputElements.length) {
      this.inputElements?.forEach((input) => {
        const fontSize = parseFloat(input.dataset.fontSize);
        this.ensureFontLoaded(input.dataset.font, fontSize);
        input.addEventListener("input", () => this.handleInputChange(input));
        input.addEventListener("focus", () => this.handleInputFocus(input));
      });
    }
    if (this.selectElement.length) {
      this.selectElement?.forEach((select) => {
        select.addEventListener("change", (event) =>
          this.handleSelectChange(event),
        );
      });
    }
    this.submitBtn?.addEventListener("click", (e) => this.handleSubmit(e));

    if (this.designMode) {
      this.designElements.forEach((input) => {
        input.addEventListener("input", () => this.handlePosition(input));
      });
    }
  }

  // --- Start Canvas ---
  loadInitialImage() {
    const image = this.querySelector(".engraving-image");

    if (image) {
      this.loadImage({ src: image.dataset.imageSrc });
    }
  }
  loadImage({ src, focusInput }) {
    this.baseImage = new Image();
    this.baseImage.crossOrigin = "anonymous";
    this.baseImage.src = src;
    this.baseImage.onload = () => {
      this.redrawCanvas(focusInput);
    };
  }
  async initCanvas() {
    this.canvas = this.querySelector("#engraving-canvas");
    if (!this.canvas) return;
    this.ctx = this.canvas.getContext("2d");
    this.adjustCanvasSize();

    if (!this.#canvasResizeBound) {
      this.#canvasResizeBound = true;
      window.addEventListener("resize", this.#onCanvasResize);
    }
  }
  adjustCanvasSize() {
    const container = this.canvas.parentElement;
    const size = Math.min(container.offsetWidth, container.offsetHeight);
    this.canvas.width = size === 0 ? 500 : size;
    this.canvas.height = size === 0 ? 500 : size;
  }
  ensureFontLoaded(fontFamily, fontSize) {
    const fontKey = `${fontFamily}-${fontSize}`;
    if (this.loadedFonts.has(fontKey)) return;
    if (this.fontLoadingPromises.has(fontKey)) {
      return this.fontLoadingPromises.get(fontKey);
    }

    const loadPromise = document.fonts
      .load(`normal ${fontSize}px "${fontFamily}"`)
      .then(() => {
        this.loadedFonts.add(fontKey);
        this.fontLoadingPromises.delete(fontKey);
      })
      .catch((err) => {
        console.warn("Font load error:", err);
        this.fontLoadingPromises.delete(fontKey);
      });

    this.fontLoadingPromises.set(fontKey, loadPromise);

    return loadPromise;
  }
  async redrawCanvas(data = null) {
    if (!this.baseImage) return;
    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    // Draw base image
    this.ctx.drawImage(
      this.baseImage,
      0,
      0,
      this.canvas.width,
      this.canvas.height,
    );

    if (!data) {
      const firstInput = this.inputElements[0];
      const firstSku = firstInput.dataset.sku;
      this.inputElements.forEach((input) => {
        if (input.dataset.sku === firstSku) {
          this.drawTextOnCanvas(input);
        }
      });
    } else {
      const sku = data.dataset.sku;
      this.inputElements.forEach((input) => {
        if (input.dataset.sku === sku) {
          this.drawTextOnCanvas(input);
        }
      });
    }
  }
  async drawTextOnCanvas(data) {
    let engravingData = null;
    let positionX = 0;
    let positionY = 0;
    let positionAngle = 0;
    let fillColor = data.dataset.engravingColor;
    engravingData = data;
    if (data.dataset.engravingType === "style") {
      const productId = data.dataset.productId;
      const selectOption = this.querySelector(
        `select[data-product-id="${productId}"]`,
      ).selectedOptions[0];
      positionX = selectOption.dataset.positionX;
      positionY = selectOption.dataset.positionY;
      positionAngle = selectOption.dataset.angle;
    } else {
      positionX = engravingData.dataset.positionX;
      positionY = engravingData.dataset.positionY;
      positionAngle = engravingData.dataset.angle;
    }
    const text = (
      engravingData.value && engravingData.value.length > 0
        ? engravingData.value
        : engravingData.placeholder
    )?.toLowerCase();
    if (!text) return;
    const x = (parseFloat(positionX) / 100) * this.canvas.width;
    const y = (parseFloat(positionY) / 100) * this.canvas.height;
    const angle = parseFloat(positionAngle);
    const fontSize = parseFloat(engravingData.dataset.fontSize);
    this.ctx.save();
    this.ctx.translate(x, y);
    this.ctx.rotate((angle * Math.PI) / 180);
    this.ctx.font = `normal ${fontSize}px "${engravingData.dataset.font || "sans-serif"}", sans-serif`;
    this.ctx.fillStyle = fillColor;
    this.ctx.textAlign = "center";
    this.ctx.textBaseline = "middle";
    this.ctx.fillText(text, 0, 0);
    this.ctx.restore();
  }
  regExpCheck({ regExp, value, input }) {
    const allowedChars = new RegExp(`^[${regExp}]*$`);
    if (!allowedChars.test(value)) {
      input.value = value.replace(new RegExp(`[^${regExp}]`, "g"), "");
    }
  }
  priceCalc(input) {
    let engravingPrice = input.dataset.engravingPrice;
    let priceCalc = input.dataset.priceCalc;
    if (input.value !== "" && priceCalc === "false") {
      let productPrice =
        parseFloat(engravingPrice) + parseFloat(this.productPrice);
      this.productPrice = productPrice.toFixed(2);
      this.productPriceElement.innerHTML = "$" + formatMoney(this.productPrice);

      input.dataset.priceCalc = "true";
    }
    if (input.value === "" && priceCalc === "true") {
      let productPrice =
        parseFloat(this.productPrice) - parseFloat(engravingPrice);
      this.productPrice = productPrice.toFixed(2);
      this.productPriceElement.innerHTML = "$" + formatMoney(this.productPrice);
      input.dataset.priceCalc = "false";
    }
  }
  handleInputFocus(input) {
    const sku = input.dataset.sku;
    const skuImage = this?.querySelector(`.engraving-image[data-sku="${sku}"]`)
      ?.dataset?.imageSrc;
    if(!skuImage) return;
    let normalizedImage = "https:" + skuImage;
    if (this.baseImage.src !== normalizedImage) {
      this.loadImage({ src: normalizedImage, focusInput: input });
    }
  }
  handleInputChange(input) {
    this.regExpCheck({
      regExp: input.dataset.allowedChars,
      value: input.value,
      input,
    });
    if (input.offsetParent.querySelector(".count-write")) {
      const countWrite = input.offsetParent.querySelector(".count-write");
      if (countWrite) countWrite.innerHTML = input.value.length;
    }
    this.priceCalc(input);
    this.redrawCanvas(input);
    this.activeInput = input;
    if(input.closest(".engraving-inputs[data-required='true']")) {
      const closetInputsNotRequired= input.closest(".engraving-inputs[data-required='true']").querySelectorAll('input:not(:required)');
      const closetInputsRequired= input.closest(".engraving-inputs[data-required='true']").querySelectorAll('input:required');
      if(closetInputsRequired.length) {
        const notEmptyRequired = [...closetInputsRequired].every(i => i.value !== '');
        if(notEmptyRequired && closetInputsNotRequired.length) {
          closetInputsNotRequired.forEach(i => i.disabled = false);
        } else {
          closetInputsNotRequired.forEach(i => {
            i.disabled = true
            i.value = '';
          });
        }
      }
    }

    this.updateButtonState();
  }
  updateButtonState(isUndisable = false) {
    if (!this.submitBtn) return;
    if (isUndisable) {
      this.submitBtn.enable();
      return;
    }

    const requiredInputs = Array.from(this.inputElements).filter(
      (input) => !input.closest(".engraving-inputs[data-required='true']") && input.required && input.value.trim() === "",
    );

    if (requiredInputs.length >= 1) {
      this.submitBtn.disable();
      return;
    }

    const hasEmpty = Array.from(this.inputElements).every(
      (input) => input.value.trim() === "",
    );
    if (hasEmpty) {
      this.submitBtn.disable();
    } else {
      this.submitBtn.enable();
    }
  }
  handleDesignChange(option) {
    // Canvas
    if (this.model === "product") {
      this.loadImage({ src: option.dataset.image });
    }
  }

  handleSelectChange(event) {
    if (!this.selectElement) return;
    let option = event.target.options[event.target.selectedIndex];
    this.handleDesignChange(option);
  }

  getDesignSelectedValue(productId) {
    const selectOption = this.querySelector(
      `select[data-product-id="${productId}"]`,
    ).selectedOptions[0];
    const sku = selectOption.value;
    const optionTitle = selectOption.innerText.trim();
    const label = selectOption.dataset.label;
    return { sku, label, optionTitle };
  }
  setEngravingProperty({ inputFieled, mainQuantity }) {
    const notes = [];
    const engravingIds = [];
    let engravingLabel = [];
    // Set up note
    inputFieled.forEach((input) => {
      const engravingType = input.dataset.engravingType;
      const productId = input.dataset.productId;
      const engravingId = input.dataset.engravingId;
      const skuNote = input.dataset.note;
      const engravingText = input.value;
      const label = input.dataset.label;
      const engravingPrice = parseFloat(input.dataset.engravingPrice);
      engravingIds.push(`${engravingId}:1:${engravingPrice}`);
      let combineNote = "";
      if (engravingType === "style") {
        const { sku, label, optionTitle } =
          this.getDesignSelectedValue(productId);
        if (skuNote) {
          combineNote = `${sku}-${skuNote}: ${engravingText}`;
        }
        combineNote = `${sku}: ${engravingText}`;
        notes.push(combineNote);
        engravingLabel[label] = optionTitle;
      }
      if (engravingType === "text") {
        combineNote = `${skuNote}: ${engravingText}`;
        notes.push(combineNote);
      }
      engravingLabel[label] = engravingText;
    });
    return { notes, engravingIds, engravingLabel };
  }
  setProductProperty({ notes, engravingLabel, mainQuantity, engravingIds }) {
    const properties = {};
    const mainFormData = new FormData(this.mainForm);
    for (const [key, value] of mainFormData.entries()) {
      const property = key.match(/^properties\[(.+)\]$/);
      if (property && value.trim().length > 0) {
        properties[property[1]] = value.trim();
      }
    }
    const noteId = Date.now().toString(36);
    const product = {
      id: this.mainProductId,
      quantity: mainQuantity,
      properties: {
        ...properties,
        ...engravingLabel,
        _engraving_ids: engravingIds.join(","),
        _notes: notes.join(","),
        _engraving_note_id: noteId,
      },
    };
    return product;
  }
  async handleSubmit(event) {
    event.preventDefault();
    const mainQuantity = parseInt(
      this.mainForm.querySelector('input[name="quantity"]')?.value || 1,
    );
    const inputFieled = Array.from(this.inputElements).filter(
      (input) => input.value.trim() !== "",
    );
    const { notes, engravingIds, engravingLabel } = this.setEngravingProperty({
      inputFieled,
      mainQuantity,
    });
    const productData = this.setProductProperty({
      notes,
      engravingLabel,
      mainQuantity,
      engravingIds,
    });
    let sections = "";
    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    sections = cartItemComponentsSectionIds.join(",");
    const productForm = {
      items: [productData, ...getAdvancedProductFormData()],
      sections,
    };

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(productForm),
      });

      const data = await response.json();
      if (response.ok) {
        // Success: Dispatch CartAddEvent
        this.dispatchEvent(
          new CartAddEvent({}, this.mainProductId, {
            source: "product-form-component",
            itemCount: mainQuantity,
            productId: this.mainProductId,
            sections: data.sections,
          }),
        );
        const dialogContent =
          this.closest("engraving-dialog-component") ??
          this.closest("dialog-component");
        dialogContent?.closeDialog();
      } else {
        // Error: Dispatch CartErrorEvent
        console.error("Error adding to cart:", data);
        this.dispatchEvent(
          new CartErrorEvent(
            this.mainForm ? this.mainForm.getAttribute("id") : "",
            data.message,
            data.description,
            data.errors,
          ),
        );
      }
    } catch (error) {
      console.error("Fetch error:", error);
      if (loading) loading.classList.add("hidden");
      if (btnTitle) btnTitle.classList.remove("hidden");
    }
  }

  handlePosition(input) {
    const note = input.dataset.note;
    const sku = input.dataset.sku;
    let thatInput = null;
    if (input.dataset.engravingType === "style") {
      thatInput = this.querySelector(`select[data-sku=${sku}]`)
        .selectedOptions[0];
    } else if (note) {
      thatInput = this.querySelector(`input[data-note=${note}]`);
    }

    if (input.name === "angle") {
      thatInput.dataset.angle = input.value;
    }
    if (input.name === "positionX") {
      thatInput.dataset.positionX = input.value;
    }
    if (input.name === "positionY") {
      thatInput.dataset.positionY = input.value;
    }
    if (input.dataset.engravingType === "style") {
      thatInput = this.querySelector(`input[data-sku=${sku}]`);
    }
    this.handleInputChange(thatInput);
  }
}
if (!customElements.get("product-engraving")) {
  customElements.define("product-engraving", ProductEngravingProduct);
}

class EngravingDialogComponent extends DialogComponent {
  showDialog() {
    const engraving = this.querySelector("product-engraving");
    if (engraving instanceof ProductEngravingProduct) {
      engraving.prepareBeforeOpen();
    }

    const { dialog } = this.refs;
    if (!dialog.open) {
      const sync = () => {
        if (engraving instanceof ProductEngravingProduct) {
          engraving.syncLayoutAfterDialogOpen();
        }
      };
      this.addEventListener(DialogOpenEvent.eventName, sync, { once: true });
    }

    super.showDialog();
  }
}

if (!customElements.get("engraving-dialog-component")) {
  customElements.define("engraving-dialog-component", EngravingDialogComponent);
}